<script setup>
import { ref, computed } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";

const { isFormDisabled } = useConsentStatus();

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage", "skip-stage"]);

const ageRanges = [
  "18-24",
  "25-29",
  "30-34",
  "35-39",
  "40-44",
  "45-49",
  "50-54",
  "55-59",
  "60+",
  "Prefer not to answer",
];

const handleNext = () => {
  emit("next-stage");
};
</script>

<template>
  <div class="survey-form">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Demographics (Optional)</h2>

    <div class="intro-text">
      <p>
        This section is completely optional. Demographic data helps us
        understand the diversity and composition of Ireland's cybersecurity
        workforce to identify opportunities for inclusive growth.
      </p>
    </div>

    <!-- Age Range -->
    <div class="form-group">
      <label for="ageRange">What is your age range? (Optional)</label>
      <select
        id="ageRange"
        v-model="surveyData.ageRange"
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select age range</option>
        <option v-for="range in ageRanges" :key="range" :value="range">
          {{ range }}
        </option>
      </select>
    </div>

    <div class="button-group">
      <button
        @click="handleNext"
        class="primary-button"
        :disabled="isFormDisabled"
      >
        Submit
      </button>
      <button @click="$emit('skip-stage')" class="skip-button">
        Skip this section
      </button>
    </div>
  </div>
</template>

<style scoped>
.intro-text {
  background-color: var(--surface-color);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-color);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: var(--text-color);
  font-size: 1rem;
}

.form-group select {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
}

.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.radio-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-weight: normal;
  cursor: pointer;
  padding: 0.5rem 0;
  min-height: 44px; /* Touch-friendly height */
  line-height: 1.4;
}

.radio-label input[type="radio"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-button:hover {
  background-color: var(--primary-hover);
}

.skip-button {
  background-color: transparent;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
}

.skip-button:hover {
  background-color: var(--surface-color);
  border-color: var(--text-color);
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .intro-text {
    padding: 0.875rem;
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    font-size: 0.95rem;
  }

  .form-group select {
    padding: 0.75rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .radio-label {
    min-height: 40px;
    font-size: 0.9rem;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
  }

  .button-group {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px;
  }
}

@media (max-width: 480px) {
  .intro-text {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group select {
    padding: 0.625rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .radio-label {
    min-height: 36px;
    font-size: 0.85rem;
    gap: 0.5rem;
  }

  .radio-label input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  .button-group {
    margin-top: 1.25rem;
    gap: 0.625rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 40px;
  }
}
</style>
